import React, {useState} from 'react';

import {getIsAntibody} from 'antibodies/AntibodyStateActions';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isSchemaTag, Tag} from 'folder-items/FileConstants';
import {HoistedTag} from 'inventory/InventoryFileConstants';
import parseProteinIdFromUrl from 'molbio/aa-sequences/util/parseAaSequenceIdFromUrl';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useOnMount from 'ui-library/hooks/useOnMount';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';

interface AnalyzeProteinsIconProps {
  onAnalyzeProteins?: ((proteinIds: Array<string>) => void) | null;
  tags: Array<Tag | null>;
  hoistedTags: Array<HoistedTag>;
  fileId?: string;
}

/**
 * Icon that analyzes properties of any proteins in the given tags on click.
 */
export default function AnalyzeProteinsIcon({
  onAnalyzeProteins,
  tags,
  hoistedTags,
  fileId,
}: AnalyzeProteinsIconProps): JSX.Element | null {
  const [isAntibody, setIsAntibody] = useState<boolean>(false);

  useOnMount(() => {
    async function fetchIsAntibody(): Promise<void> {
      if (fileId != null) {
        const isAntibody = await getIsAntibody(fileId);
        setIsAntibody(isAntibody);
      }
    }
    fetchIsAntibody();
  });

  /**
   * Return all writable tags that are not staged for deletion.
   */
  function getEditableTags(): Array<Tag> {
    return tags.filter((tag: Tag | null): tag is Tag => tag != null);
  }

  /**
   * Displayed tags include both writable tags that have not been staged for deletion,
   * and read-only hoisted tags.
   */
  function getAllDisplayedTags(): Array<Tag> {
    return getEditableTags().concat(hoistedTags);
  }

  function getProteinIds(): Array<string> {
    const proteinIds: Array<string | null> = [];
    for (const tag of getAllDisplayedTags()) {
      if (tag.url) {
        proteinIds.push(parseProteinIdFromUrl(tag.url));
      }
      if (isSchemaTag(tag) && tag.targetFiles) {
        for (const targetFile of tag.targetFiles) {
          if (targetFile.id != null && APIUtil.apiIdToType(targetFile.id) === ModelTypeConstants.PROTEIN) {
            proteinIds.push(targetFile.id);
          }
        }
      }
    }
    return proteinIds.filter((id) => Boolean(id)) as Array<string>;
  }

  if (
    BenchlingConfig.MOLBIO_HIDE_ANALYZE_AA_SEQUENCES ||
    isAntibody ||
    onAnalyzeProteins == null ||
    getProteinIds().length === 0
  ) {
    return null;
  }

  return (
    <Tooltip text="Analyze AA Sequences">
      <div>
        <BenchlingIcon
          className="metadata-analyzeButton u-pointer u-small-spacing-left"
          icon="aa-analyze"
          onClick={() => {
            onAnalyzeProteins(getProteinIds());
          }}
        />
      </div>
    </Tooltip>
  );
}
